// Work orders (OT) board: lanes, filters and aging -- pure, tested by
// otLogica.test.mjs. The states come from the server (ot_config.py); a lane
// groups the states that share a stage of the flow.

export const CARRILES = [
  { id: 'solicitadas', estados: ['solicitada', 'propuesta'], fondo: 'bg-amber-50', acento: 'border-t-amber-400' },
  { id: 'devueltas', estados: ['devuelta'], fondo: 'bg-rose-50', acento: 'border-t-rose-400' },
  { id: 'planificadas', estados: ['planificada'], fondo: 'bg-sky-50', acento: 'border-t-sky-400' },
  // Liberada (OF in SAP) is where the kit is prepared (subestado espera_componentes).
  { id: 'liberadas', estados: ['liberada'], fondo: 'bg-indigo-50', acento: 'border-t-indigo-400' },
  // From the kit's delivery to finishing (mark recibido_conforme).
  { id: 'ejecucion', estados: ['en_ejecucion'], fondo: 'bg-emerald-50', acento: 'border-t-emerald-400' },
  { id: 'terminadas', estados: ['terminada'], fondo: 'bg-teal-50', acento: 'border-t-teal-400' },
  { id: 'cerradas', estados: ['cerrada'], fondo: 'bg-gray-100', acento: 'border-t-gray-400', final: true },
  { id: 'canceladas', estados: ['cancelada'], fondo: 'bg-gray-100', acento: 'border-t-gray-300', final: true },
]

// What an OT is about, in one line: the item it makes, the parte it works, or
// -- a service OT (OT-<n>S) -- what is to be done. null when nothing says.
export function tituloOt(o) {
  return o.articulo_descripcion || o.articulo_codigo || o.parte?.nombre || o.descripcion || null
}

// Planning: the responsable is one of the chosen group's people.
// miembrosDe -> the group's people ([] without a group); responsableTrasGrupo
// -> who the responsable is after choosing a group: the same if they're in it,
// else the group's supervisor, else nobody (''). Ids as strings, like a select.
export const miembrosDe = (grupos, grupoId) => grupos.find(g => String(g.id) === String(grupoId))?.miembros || []
export function responsableTrasGrupo(grupos, grupoId, responsableId) {
  const g = grupos.find(x => String(x.id) === String(grupoId))
  if (!g) return ''
  if (responsableId && g.miembros.some(m => String(m.id) === String(responsableId))) return String(responsableId)
  return g.supervisor ? String(g.supervisor.id) : ''
}

// A card's colour, from its traffic light: a strong left edge and a soft
// tint, so a list reads at a glance. Neutral when there is no light.
const TONOS = {
  verde: 'border-l-emerald-500 bg-emerald-50/60 hover:bg-emerald-50',
  amarillo: 'border-l-amber-400 bg-amber-50/60 hover:bg-amber-50',
  rojo: 'border-l-rose-500 bg-rose-50/70 hover:bg-rose-50',
}
export const tonoDe = (semaforo) => TONOS[semaforo?.color] || 'border-l-gray-300 bg-white hover:bg-gray-50'

// The rejected SAP task holding a planned OT back, if any: the newest task of
// its transaction is rejected (an older rejection replaced by a newer task
// no longer counts).
export function rechazoAbierto(ot) {
  if (!ot || ot.estado !== 'planificada') return null
  const ultima = {}
  for (const t of [...(ot.tareas_sap || [])].sort((a, b) => a.id - b.id)) {
    if (t.estado !== 'anulada') ultima[t.transaccion] = t
  }
  return Object.values(ultima).find(t => t.estado === 'rechazada') || null
}

// The admission tray colours by origin (everything there is ready to review).
export const tonoOrigen = (origen) => (origen === 'reposicion'
  ? 'border-l-orange-400 bg-orange-50/60 hover:bg-orange-50'
  : 'border-l-sky-400 bg-sky-50/50 hover:bg-sky-50')

// OTs by lane, each newest first; drafts apart (only the user's own come).
// `ocultar`: lane ids left out (e.g. devueltas, see verDevueltas).
export function agrupar(ots, { verFinales = false, ocultar = [] } = {}) {
  const carriles = CARRILES.filter(c => (verFinales || !c.final) && !ocultar.includes(c.id))
    .map(c => ({ ...c, ots: ots.filter(o => c.estados.includes(o.estado)).sort((a, b) => b.id - a.id) }))
  const borradores = ots.filter(o => o.estado === 'borrador').sort((a, b) => b.id - a.id)
  return { carriles, borradores }
}

// The "Devueltas" lane shows only to whoever has one of their own there,
// or ticks the box to see it.
export function verDevueltas(ots, yoId, marcado = false) {
  return marcado || ots.some(o => o.estado === 'devuelta' && o.solicitante?.id === yoId)
}

// Text search (OT-n, item, description, project, requester, folio), origin,
// project and "only mine".
// tipo: '' | 'fabricacion' (fabrication and structures) | 'mantenimiento' (a parte's OT).
export function filtrar(ots, { buscar = '', origen = '', proyectoId = '', soloMias = false, yoId = null, tipo = '' } = {}) {
  const b = buscar.trim().toLowerCase()
  return ots.filter(o =>
    // fabricación takes estructura too
    (!tipo || (tipo === 'fabricacion' ? ['fabricacion', 'estructura'].includes(o.tipo) : o.tipo === tipo)) &&
    (!origen || o.origen === origen) &&
    (!proyectoId || String(o.proyecto?.id) === String(proyectoId)) &&
    (!soloMias || o.solicitante?.id === yoId) &&
    // a batch by its exact code (L-1 isn't L-12)
    (!b || o.lote?.codigo.toLowerCase() === b || [o.codigo, o.articulo_codigo, o.articulo_descripcion, o.descripcion, o.proyecto?.name,
      o.proyecto?.code, o.solicitante?.display_name, o.folio_of,
      o.parte && `#${o.parte.numero}`, o.parte?.unidad_codigo, o.parte?.nombre]
      .some(v => v && String(v).toLowerCase().includes(b))))
}

// Whole days since `desde` (an ISO datetime from the server, UTC).
export function diasDesde(desde, ahoraMs = Date.now()) {
  if (!desde) return null
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(desde) ? desde : `${desde}Z`)
  return Math.max(0, Math.floor((ahoraMs - t) / 86400000))
}

// A requested date already past (and the OT still open)?
export function vencida(ot, hoyIso) {
  return !!ot.fecha_requerida && ot.fecha_requerida < hoyIso && !['cerrada', 'cancelada'].includes(ot.estado)
}

// What sending a request needs, checked before going to the server (which
// checks again): the same names the engine answers in detail.faltan.
export function faltantes(f, { adjuntos = [] } = {}) {
  const faltan = []
  if (!String(f.articulo_codigo || '').trim()) faltan.push('articulo_codigo')   // SKU / IDREM: ties the OT to its OF
  if (!(Number(f.cantidad) > 0)) faltan.push('cantidad')
  if (!f.fecha_requerida) faltan.push('fecha_requerida')
  if (f.tiene_receta_sap == null) faltan.push('tiene_receta_sap')
  if (f.origen === 'proyecto' && !f.proyecto_id) faltan.push('proyecto')
  if (f.tiene_receta_sap === false) {
    const tipos = new Set(adjuntos.map(a => a.tipo))
    for (const t of ['bom', 'plano']) if (!tipos.has(t)) faltan.push(`adjunto_${t}`)
  }
  return faltan
}

// Several OF PDFs dropped at once: archivos = [{ file, coincide: [tareaId] }]
// (the tasks each one matches). A file goes straight to its task when it
// matches exactly one, no other dropped file matches that task too, and the
// task holds no file yet (abiertos: { tareaId: file }). Returns the new
// abiertos; the rest stay for the user to pick.
export function repartirPdfs(archivos, abiertos = {}) {
  const reclamos = {}
  archivos.forEach(a => a.coincide.forEach(id => { reclamos[id] = (reclamos[id] || 0) + 1 }))
  const nuevo = { ...abiertos }
  archivos.forEach(a => {
    const id = a.coincide[0]
    if (a.coincide.length === 1 && reclamos[id] === 1 && !nuevo[id]) nuevo[id] = a.file
  })
  return nuevo
}
